/** One-shot facts for presentation, and the tick rate. */

import type { BarkKind } from './enemy';
import type { EnemyKind } from './mission';
import type { TimedEffect } from './objective';
import type { OspreyPhase, Outcome } from './world';

/** One-shot facts for presentation (render, audio, UI). The sim never reads them back. */
export type SimEvent =
  /** `suppressed`: the ranger's silenced pistol - a cough, not a report (absent on every other shot). */
  | { type: 'shot'; fromPlayer: boolean; x: number; z: number; dirX: number; dirZ: number; emplacement: boolean;
      suppressed?: boolean }
  | { type: 'impact'; x: number; z: number; y: number }
  | { type: 'knife'; x: number; z: number; hit: boolean }
  | { type: 'throw'; x: number; z: number }
  | { type: 'rocket'; x: number; z: number }
  | { type: 'explosion'; x: number; z: number; radius: number; big: boolean }
  | { type: 'propDestroyed'; id: number }
  | { type: 'enemyHit'; id: number; killed: boolean }
  | { type: 'rangerHit'; wounded: boolean }
  | { type: 'rangerDowned' }
  | { type: 'alert'; level: number }
  | { type: 'spotted'; id: number }
  | { type: 'pickup'; what: string }
  | { type: 'dryFire' }
  | { type: 'reload' }
  | { type: 'bombPlaced'; x: number; z: number; fuse: number }
  | { type: 'objective'; destroyed: number; total: number }
  | { type: 'osprey'; phase: OspreyPhase }
  | { type: 'reinforcements'; count: number }
  | { type: 'bark'; id: number; x: number; z: number; kind: BarkKind }
  | { type: 'siren' }
  | { type: 'objectiveFailed'; reason: string }
  | { type: 'objectiveComplete' }
  | { type: 'captive'; id: number; what: 'freed' | 'boarded' | 'lost' | 'surrendered' }
  | { type: 'disguise'; on: boolean }
  | { type: 'mine'; x: number; z: number }
  | { type: 'iceBreak'; x: number; z: number }
  | { type: 'signal' }
  | { type: 'podDropped'; index: number }
  | { type: 'podLanded'; index: number; intact: boolean }
  | { type: 'podOpened'; index: number }
  | { type: 'propRemoved'; id: number }
  | { type: 'smoke'; x: number; z: number }
  | { type: 'tagsRecovered' }
  /**
   * A `timedEvent` rule (10 §3). `fired`: it happened now. `hastened`: the alert brought it forward (`seconds`
   * = what is left). `windowClosed`: a passing convoy has passed. `rule` indexes `def.objective.rules`.
   */
  | { type: 'timedEvent'; effect: TimedEffect; phase: 'fired' | 'hastened' | 'windowClosed'; rule: number; seconds: number }
  /** A blackout group's switch was thrown or destroyed: that many searchlights and automated guns went dead. */
  | { type: 'blackout'; group: number; lights: number; guns: number }
  /**
   * A vehicle (ROUND4 §1). `left`: it drove off the end of its run and is off the map - the despawn, so the
   * engine note can fade and the HUD stop counting it. `wrecked`: it is a burning hulk in the road.
   * `dismount`: `count` men got down from it (0 = they all went up with it).
   */
  | { type: 'vehicle'; id: number; kind: EnemyKind; what: 'left' | 'wrecked' | 'dismount'; count: number; x: number; z: number }
  | { type: 'jump' }
  | { type: 'landed'; hazard: 'none' | 'rough' | 'wire' | 'water' | 'mine' | 'badlands' }
  | { type: 'ended'; outcome: Outcome };

export const TICK_RATE = 60;

export const DT = 1 / TICK_RATE;
